import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  DilationSolution,
  RatioKnown,
  SpeedSolution,
  TripSolution,
} from "@/lib/tool-kit/physics-fields/relativity";

/** Equations with the numbers substituted, in SI (times in seconds). */

export function dilationWorking(
  s: DilationSolution,
  fmt: NumberFormatter,
  sig: number,
  hasTime: boolean,
  hasLength: boolean
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines = [
    `β = v / c = ${e(s.speed)} / 299792458 = ${formatSig(fmt, s.beta, Math.max(sig, 5))}`,
    `γ = 1 / √(1 − β²) = 1 / √((1 − β)(1 + β)) = ${formatSig(fmt, s.gamma, Math.max(sig, 6))}`,
    `γ − 1 = γ²β² / (γ + 1) = ${formatSig(fmt, s.excess, sig)}`,
  ];
  if (hasTime) {
    lines.push(
      `Δt = γ Δt₀ = ${formatSig(fmt, s.gamma, Math.max(sig, 6))} × ${e(s.properTime)} = ${r(s.dilatedTime, "s")}`,
      `Δt − Δt₀ = (γ − 1) Δt₀ = ${r(s.drift, "s")}`
    );
  }
  if (hasLength) {
    lines.push(
      `L = L₀ / γ = ${e(s.properLength)} / ${formatSig(fmt, s.gamma, Math.max(sig, 6))} = ${r(s.contractedLength, "m")}`
    );
  }
  return lines;
}

export function speedWorking(
  known: RatioKnown,
  s: SpeedSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const lines: string[] = [];
  if (known === "time") {
    lines.push(`γ = Δt / Δt₀ = ${formatSig(fmt, s.gamma, Math.max(sig, 6))}`);
  } else if (known === "length") {
    lines.push(`γ = L₀ / L = ${formatSig(fmt, s.gamma, Math.max(sig, 6))}`);
  }
  lines.push(
    `β = √(γ² − 1) / γ = √((γ − 1)(γ + 1)) / γ = ${formatSig(fmt, s.beta, Math.max(sig, 6))}`,
    `v = β c = ${formatSig(fmt, s.speed, sig)} m/s`
  );
  return lines;
}

export function tripWorking(
  s: TripSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  return [
    `γ = 1 / √(1 − β²) = ${formatSig(fmt, s.gamma, Math.max(sig, 6))}   (β = ${formatSig(fmt, s.beta, Math.max(sig, 5))})`,
    `Earth: t = 2d / v = 2 × ${e(s.distance)} / ${e(s.beta * 299_792_458)} = ${r(s.earthTime, "s")}`,
    `Traveller: τ = t / γ = ${e(s.earthTime)} / ${formatSig(fmt, s.gamma, Math.max(sig, 6))} = ${r(s.travellerTime, "s")}`,
    `Distance in the traveller's frame: d / γ = ${r(s.contractedDistance, "m")}`,
  ];
}
